import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  OauthAEMConfig,
  PkceCode,
  RememberMeWrapperService,
  UtilService
} from '@com-delta-idp/mnx-oauth';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { share } from 'rxjs/operators';
import { CookieHelper } from '../common/cookie-helper';
import { ApplicationConfig } from '../config/config';
import { TokenDetails } from '../model/auth-config.model';
import { CommonService } from './common.service';
import { SKYMILES_LOGIN_URL } from './login-service-worker.constant';

@Injectable({
  providedIn: 'root'
})
export class LoginServiceWorkerService {
  accessToken = '';
  eamStatus = '';
  tokenExpiry: number;
  tokenIssuedTime: number;

  headerConfigJson!: Observable<OauthAEMConfig>;

  pkceCode!: PkceCode;
  // TODO: change needs to be done in the oauth library for defining a proper interface
  tokenDetails;

  tokenValue$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private rememberMeWrapperService: RememberMeWrapperService,
    private utils: UtilService,
    private http: HttpClient,
    private cookieHelper: CookieHelper,
    private config: ApplicationConfig,
    private loggingService: LoggingService,
    private common: CommonService
  ) {}

  /*
   * if the service worker is registered and active, it gets the token value from service worker. Otherwise, it would make ping calls to get a token if there is a valid session
   */
  async getToken(response: OauthAEMConfig) {
    if (isPlatformBrowser(this.platformId)) {
      if (this.checkServiceWorkerController()) {
        await this.setValuesFromServiceWorker(response);
        await this.setStatusValueFromServiceWorker(response);
      }

      await this.makePingCall(response);
    }
  }

  /*
   * register the service worker by check if the browser supports it and if the flag is true. if flag is false, then it unregisters the service worker and reloads the page
   */
  registerServiceWorker(response: OauthAEMConfig) {
    // TODO: B-671207 will refactor the below condition in a utility function
    const origin = window.location.origin;
    const modifiedOrigin = origin.replace(/^https?:\/\//, '');
    const scope = modifiedOrigin.split('/')[0];
    if ('serviceWorker' in navigator) {
      if (response?.ENABLE_NEW_PERSISTENT_LOGIN) {
        try {
          navigator.serviceWorker
            .register(response?.REGISTER_SW_URL, {
              scope: 'https://' + scope,
              updateViaCache: 'none'
            })
            .then(function () {
              return navigator.serviceWorker.ready;
            });
        } catch (e) {
          this.loggingService.log(
            'The registration for service workers failed: ' + e
          );
        }
      } else {
        navigator.serviceWorker
          .getRegistrations()
          .then(function (registrations) {
            for (let registration of registrations) {
              registration.unregister();
              this.loggingService.log(
                'The service workers has been unregistered.'
              );
              window.location.reload();
            }
          });
      }
    }
  }

  /*
   * sets token values from the service worker, if available and dispatches an event
   */
  async setValuesFromServiceWorker(response: OauthAEMConfig) {
    if (response?.ENABLE_NEW_PERSISTENT_LOGIN) {
      await this.sendMessage({ type: 'GET_TOKEN' }).then(
        async (data: TokenDetails) => {
          if (data?.token) {
            this.accessToken = await data?.token;
            this.tokenExpiry = await data?.expiresIn;
            this.tokenIssuedTime = await data?.issueTime;

            if (
              !(this.accessToken && this.checkTokenExpiryTime() <= 5 * 60000)
            ) {
              this.tokenValue$.next(true);
              const event = new CustomEvent('service-worker-token', {
                bubbles: true
              });
              document.dispatchEvent(event);
            }
          }
        }
      );
    }
  }

  /*
   * sets status value from the service worker, if available
   */
  async setStatusValueFromServiceWorker(response: OauthAEMConfig) {
    if (response?.ENABLE_NEW_PERSISTENT_LOGIN) {
      await this.sendMessage({ type: 'GET_STATUS' }).then(
        async (data: TokenDetails) => {
          if (data?.eamStatus) {
            this.eamStatus = await data?.eamStatus;
          }
        }
      );
    }
  }

  /*
   * deletes token values from the service worker
   */
  async deleteValuesFromServiceWorker(response: OauthAEMConfig) {
    if (response && response?.ENABLE_NEW_PERSISTENT_LOGIN) {
      await this.sendMessage({ type: 'DELETE_TOKEN' }).then(
        async (data: TokenDetails) => {
          if (data) {
            this.accessToken = (await data?.token) ? data.token : '';
            this.tokenExpiry = (await data?.expiresIn) ? data.expiresIn : 0;
            this.tokenIssuedTime = (await data?.issueTime) ? data.issueTime : 0;
            this.tokenValue$.next(false);
          }
        }
      );
    }
  }

  /*
   * deletes status value from the service worker
   */
  async deleteStatusValuesFromServiceWorker(response: OauthAEMConfig) {
    if (response && response?.ENABLE_NEW_PERSISTENT_LOGIN) {
      await this.sendMessage({ type: 'DELETE_STATUS' }).then(
        async (data: TokenDetails) => {
          if (data) {
            this.eamStatus = (await data?.eamStatus) ? data.eamStatus : '';
          }
        }
      );
    }
  }

  /*
   * make ping calls if the token values are not present in the service worker or if the login
   */
  async makePingCall(response: OauthAEMConfig, sessionTimeOut = false) {
    if (!window.location.href.includes(SKYMILES_LOGIN_URL)) {
      if (
        !this.accessToken ||
        (this.accessToken && this.checkTokenExpiryTime() <= 5 * 60000) ||
        sessionTimeOut
      ) {
        if (response?.ENABLE_NEW_PERSISTENT_LOGIN && !this.eamStatus) {
          this.pkceCode = this.utils.generateCodeChallenge();
          this.tokenDetails =
            await this.rememberMeWrapperService.initiatePingFlow(
              response,
              this.pkceCode
            );

          if (
            this.tokenDetails?.loginAuthWithCredentials?.status ===
            response?.USERNAME_PASSWORD_REQUIRED
          ) {
            this.sendMessage({
              type: 'ADD_STATUS',
              eamStatus: this.tokenDetails?.loginAuthWithCredentials?.status
            });
            if (this.accessToken) {
              await this.deleteValuesFromServiceWorker(response);
            }
          }

          if (
            this.tokenDetails?.access_token &&
            this.checkServiceWorkerController()
          ) {
            this.sendMessage({
              type: 'ADD_TOKEN',
              token: this.tokenDetails?.access_token,
              issueTime: new Date().getTime(),
              expiresIn: this.tokenDetails?.expires_in
            }).then(() => {
              if (this.tokenDetails?.access_token) {
                this.tokenValue$.next(true);
                const event = new CustomEvent('service-worker-token', {
                  bubbles: true
                });
                document.dispatchEvent(event);
              }
            });
          } else {
            if (
              window.localStorage.getItem('uid') ||
              window.localStorage.getItem('encyToken') ||
              this.cookieHelper.getCookies('accountType')
            ) {
              window.localStorage.removeItem('uid');
              window.localStorage.removeItem('encyToken');
              this.cookieHelper.deleteCookie('accountType');

              this.tokenValue$.next(true);
              const event = new CustomEvent('service-worker-token', {
                bubbles: true
              });
              document.dispatchEvent(event);
            }
          }
        }
      }
    }
  }

  checkTokenExpiryTime() {
    const tokenValidityTime = this.tokenIssuedTime + this.tokenExpiry * 1000;
    return tokenValidityTime - new Date().getTime();
  }

  sendMessage(message: {
    type: string;
    token?: string;
    issueTime?: number;
    expiresIn?: number;
    eamStatus?: string;
  }) {
    return new Promise(function (resolve, reject) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = function (event) {
        if (event.data.error) {
          reject(event.data.error);
        } else {
          resolve(event.data);
        }
      };

      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message, [
          messageChannel.port2
        ]);
      }
    });
  }

  getHeaderConfigJson(): Observable<OauthAEMConfig> {
    if (this.headerConfigJson) {
      return this.headerConfigJson;
    } else {
      this.headerConfigJson = from(this.common.getConfigFromAem());
      return this.headerConfigJson;
    }
  }

  getHeaderConfigJsonData() {
    return this.http
      .get<OauthAEMConfig>(`${this.config.get('APP_AUTH_CONFIG_JSON')}`)
      .pipe(share());
  }

  /*
   * checks if service worker is active and there is a logged in session
   */
  isServiceWorker() {
    if (
      this.checkServiceWorkerController() &&
      (this.cookieHelper.getCookies('accountType') ||
        (window.localStorage.getItem('uid') &&
          window.localStorage.getItem('encyToken')))
    ) {
      return true;
    }
  }

  getServiceWorkerCheck(): boolean {
    if (
      (this.isServiceWorker() && this.tokenValue$.value) ||
      !this.isServiceWorker()
    ) {
      return true;
    }
    return false;
  }

  checkServiceWorkerController() {
    return navigator.serviceWorker && navigator.serviceWorker.controller;
  }
}
